import { useState } from "react";
import { ApiError } from "../../api/client";
import { isValidEstimatePayload, refinementQueueIfValid, refinementResultIfValid } from "../../api/estimate";
import { newIdempotencyKey } from "../../api/planner";
import type { EstimatePreview, MissingInput, PlanItem, RefinementResult } from "../../types";
import { refinementAnnouncement } from "./refinementCopy";

/**
 * The shape both `refineQuestionnaire` and `refinePlan` reduce to once their
 * host-specific first argument (a session id or a public id) is bound —
 * see `frontend/src/api/planner.ts`. Parameterising on this, rather than on
 * the session/plan id itself, is what makes the anonymous-preview host and
 * the saved-plan host behave identically by construction. See
 * docs/architecture/features/W4-03.md §2.
 */
export type RefinementSubmit = (
  inputVersion: string,
  inputCode: string,
  value: unknown,
  idempotencyKey?: string,
) => Promise<EstimatePreview>;

interface UseRefinementFlowArgs {
  initialEstimate: EstimatePreview;
  /** The input targeted by a card-level `Improve my estimates` CTA, if any. Only consulted before the first answer. */
  requestedCode?: string;
  submit: RefinementSubmit;
  announce: (message: string) => void;
}

export interface RefinementFlowError {
  code: string;
  message: string;
}

export interface UseRefinementFlowResult {
  /** The current server snapshot — the only authority for what renders. */
  estimate: EstimatePreview;
  /** The validated, server-owned queue of blockers in this snapshot. Never reordered or filtered by any client rule beyond §skip/§asked-once below. */
  queue: MissingInput[];
  /** The question to show now, or `null` when nothing left to ask this pass. */
  current: MissingInput | null;
  /** How many questions are available right now (askable, not skipped, not already answered this pass) — includes `current`. */
  remaining: number;
  /** The 1-based count of this question within the whole session, cumulative across every accepted answer. */
  position: number;
  answeredCount: number;
  /** `false` on the last available question — skipping it would be a no-op dressed as a choice. */
  canSkip: boolean;
  /** Items in the current snapshot that are currently blocked by `current`, derived from `items`, never from the broader `consumers` list which also names already-calculated items. */
  blockedBy: PlanItem[];
  /** What the most recently accepted answer changed. `undefined` before any answer in this flow; `null` means "recalculated, uncharacterised". */
  lastResult: RefinementResult | null | undefined;
  /** Distinct item codes changed across every answer accepted this session — for the completion summary, never double-counted. */
  totalChangedItems: number;
  /** Skipped codes that are still live blockers in the current queue — the honest "still waiting" count, excluding anything skip resolved away on its own. */
  skippedRemaining: number;
  busy: boolean;
  error: RefinementFlowError | null;
  submitAnswer: (value: unknown) => Promise<void>;
  skip: () => void;
}

function computeAvailable(
  queue: MissingInput[],
  askedCodes: string[],
  skippedCodes: Set<string>,
): MissingInput[] {
  return queue.filter((blocker) => !skippedCodes.has(blocker.code) && !askedCodes.includes(blocker.code));
}

export function useRefinementFlow({
  initialEstimate,
  requestedCode,
  submit,
  announce,
}: UseRefinementFlowArgs): UseRefinementFlowResult {
  const [estimate, setEstimate] = useState(initialEstimate);
  // Session-scoped, memory-only, never persisted — see
  // docs/architecture/features/W4-03.md §7. Cleared codes are re-eligible;
  // see the invalidation handling in `submitAnswer` below.
  const [askedCodes, setAskedCodes] = useState<string[]>([]);
  const [skippedCodes, setSkippedCodes] = useState<Set<string>>(new Set());
  const [answeredCount, setAnsweredCount] = useState(0);
  const [lastResult, setLastResult] = useState<RefinementResult | null | undefined>(undefined);
  const [changedItemCodes, setChangedItemCodes] = useState<Set<string>>(new Set());
  // At most one unsent answer ever exists, and it is never stored here — it
  // lives in the question component's own form state. This object holds
  // only the fingerprint/key pair for the *last attempted* submission, so a
  // retry of the same (input_version, code, value) reuses its idempotency
  // key rather than minting a new one. See
  // docs/architecture/features/W4-03.md §5.
  const [pending, setPending] = useState<{ fingerprint: string; key: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<RefinementFlowError | null>(null);

  const queue = refinementQueueIfValid(estimate);
  const available = computeAvailable(queue, askedCodes, skippedCodes);
  const current =
    answeredCount === 0 && requestedCode
      ? (available.find((blocker) => blocker.code === requestedCode) ?? available[0] ?? null)
      : (available[0] ?? null);
  const remaining = available.length;
  const position = answeredCount + 1;
  const canSkip = remaining > 1;
  const blockedBy = current
    ? estimate.items.filter((item) => item.missing_input?.code === current.code)
    : [];
  const skippedRemaining = queue.filter((blocker) => skippedCodes.has(blocker.code)).length;

  async function submitAnswer(value: unknown) {
    if (!current || busy) return;
    const fingerprint = JSON.stringify([estimate.input_version, current.code, value]);
    const key = pending?.fingerprint === fingerprint ? pending.key : newIdempotencyKey();
    setPending({ fingerprint, key });
    setBusy(true);
    setError(null);

    try {
      const updated = await submit(estimate.input_version, current.code, value, key);
      const result = refinementResultIfValid(updated.refinement_result);
      const invalidated = result?.invalidated_input_codes ?? [];
      const nextQueue = refinementQueueIfValid(updated);
      const answeredCode = current.code;

      const nextAskedCodes = [...askedCodes.filter((code) => !invalidated.includes(code)), answeredCode];
      const nextSkippedCodes = new Set(skippedCodes);
      for (const code of invalidated) nextSkippedCodes.delete(code);
      const stillAvailable = computeAvailable(nextQueue, nextAskedCodes, nextSkippedCodes);

      setEstimate(updated);
      setAskedCodes(nextAskedCodes);
      setSkippedCodes(nextSkippedCodes);
      setAnsweredCount((count) => count + 1);
      setLastResult(result);
      if (result) {
        setChangedItemCodes((previous) => {
          const next = new Set(previous);
          for (const code of result.changed_item_codes) next.add(code);
          return next;
        });
      }
      setPending(null);
      const message = refinementAnnouncement(result, updated.items, nextQueue);
      announce(stillAvailable.length === 0 ? `${message} All questions answered.` : message);
    } catch (caught) {
      handleFailure(caught, current.code);
    } finally {
      setBusy(false);
    }
  }

  function handleFailure(caught: unknown, attemptedCode: string) {
    if (!(caught instanceof ApiError)) {
      setError({ code: "unknown_error", message: "We couldn’t update that estimate. Your other results are unchanged." });
      return;
    }

    if (caught.status === 409 && caught.code === "stale_input_version") {
      // The old key was fingerprinted against an `input_version` that is
      // now stale; a resubmission necessarily uses a new version and so
      // needs a new key regardless. Clearing it here is what makes that
      // automatic on the next attempt.
      setPending(null);
      if (isValidEstimatePayload(caught.latest)) {
        const latest = caught.latest;
        const stillBlocks = refinementQueueIfValid(latest).some((blocker) => blocker.code === attemptedCode);
        setEstimate(latest);
        setError({
          code: caught.code,
          message: stillBlocks
            ? "This plan changed somewhere else. We’ve loaded the latest version — your answer is still here."
            : "Someone already answered this one. We’ve moved you to the next question.",
        });
      } else {
        setError({
          code: caught.code,
          message: "This plan changed somewhere else. Refresh to see the latest version.",
        });
      }
      return;
    }

    if (caught.status === 409) {
      // idempotency_conflict — never auto-retry.
      setPending(null);
      setError({
        code: caught.code,
        message: "That update didn’t go through cleanly. Try sending this answer again.",
      });
      return;
    }

    if (caught.status === 422) {
      // Existing behaviour, unchanged: the server's own safe message,
      // surfaced against the current question. The answer is retained by
      // the caller's own form state; nothing here advances.
      setError({ code: caught.code, message: caught.message });
      return;
    }

    if (
      caught.status === 429
      || caught.status === 503
      || caught.code === "network_error"
      || caught.code === "offline"
    ) {
      // The rate limiter and a lost connection both run before any snapshot
      // can be created, so retaining the key for a retry is always safe —
      // see docs/architecture/features/W4-03.md §5. `pending` is left
      // untouched (not cleared) so the retry reuses the same key.
      setError({ code: caught.code, message: caught.message });
      return;
    }

    if (caught.code === "invalid_estimate_payload") {
      // The response could not be trusted. We don't know whether the
      // mutation itself landed, so the key is retained exactly as for a
      // network failure — the last good `estimate` stays authoritative.
      setError({
        code: caught.code,
        message: "We couldn’t safely load the updated plan. Your answer is still here — try again.",
      });
      return;
    }

    if (caught.status === 404) {
      // session_not_found / plan_not_found — the one case the host should
      // leave the flow for. We surface the code; navigation is the host's
      // responsibility, not this hook's.
      setPending(null);
      setError({ code: caught.code, message: caught.message });
      return;
    }

    setPending(null);
    setError({ code: caught.code, message: caught.message });
  }

  function skip() {
    if (!current || !canSkip) return;
    setSkippedCodes((previous) => new Set(previous).add(current.code));
    announce("Question skipped.");
  }

  return {
    estimate,
    queue,
    current,
    remaining,
    position,
    answeredCount,
    canSkip,
    blockedBy,
    lastResult,
    totalChangedItems: changedItemCodes.size,
    skippedRemaining,
    busy,
    error,
    submitAnswer,
    skip,
  };
}
